export type ThemeMode = 'light' | 'dark' | 'auto';

//extra method added into to avoid gap between theme loading
function waitForStylesheet(link: HTMLLinkElement): Promise<void> {
  return new Promise((resolve) => {
    if (link.sheet) return resolve();

    const done = () => {
      link.removeEventListener('load', done);
      link.removeEventListener('error', done);
      resolve();
    };

    link.addEventListener('load', done);
    link.addEventListener('error', done);
  });
}

export async function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;

  // If this page is locked, force that mode and do NOT toggle stylesheets/localStorage
  const lock = root.getAttribute('data-theme-lock') as ThemeMode | null;
  if (lock === 'light' || lock === 'dark') {
    mode = lock;
  }

  const light = document.getElementById('antd-light') as HTMLLinkElement | null;
  const dark = document.getElementById('antd-dark') as HTMLLinkElement | null;

  if (!light || !dark) return;

  //check for auto, overwrite and use that when able
  if (mode === 'auto') {
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    mode = prefersDark ? 'dark' : 'light';
  }

  const next = mode === 'dark' ? dark : light;
  const prev = mode === 'dark' ? light : dark;

  // Only change localStorage if not locked (optional but usually desired)
  const isLocked = lock === 'light' || lock === 'dark';
  if (!isLocked) {
    localStorage.setItem('theme', mode);
  }

  const wasDisabled = next.disabled;
  if (wasDisabled) next.disabled = false;

  await waitForStylesheet(next);
  next.disabled = false;
  prev.disabled = true;

  localStorage.setItem('theme', mode);
  document.documentElement.setAttribute('data-theme', mode);
}

// basic call when app is first ran and on logout, should just apply the light theme
export function initTheme() {
  applyTheme('auto');
}
